/**
 * Backend User Profiles Service
 * Handles all user profile and client-related API calls to the FastAPI backend.
 * Uses simplified X-User-Id authentication for Azure deployment.
 */

import { apiRequest } from '@/lib/api-client'
import { logError, logInfo } from '@/lib/logger'

// ============== Types ==============

export interface ClientInfo {
  id: string
  name: string
}

export interface UserProfile {
  id: string
  role: 'admin' | 'user'
  is_active: boolean
  client_id: string | null
  approved_at: string | null
  approved_by: string | null
  created_at: string
  updated_at: string
  email: string | null
  client: ClientInfo | null
  allowed_applications: string[]
}

export interface UserProfileUpdate {
  role?: 'admin' | 'user'
  is_active?: boolean
  client_id?: string
}

export interface Client {
  id: string
  name: string
  default_theme_id: string | null
  created_at: string
  updated_at: string
}

export interface ClientCreate {
  name: string
  default_theme_id?: string
}

export interface ClientUpdate {
  name?: string
  default_theme_id?: string
}

interface UserProfileListResponse {
  users: UserProfile[]
  total: number
}

interface ClientListResponse {
  clients: Client[]
  total: number
}

// ============== Backend User Profiles Service ==============

export const backendUserProfilesService = {
  // ============== User Profiles ==============

  async getAllUsers(activeOnly: boolean = false): Promise<UserProfile[]> {
    try {
      const params = activeOnly ? '?active_only=true' : ''
      const response = await apiRequest<UserProfileListResponse>(`/users${params}`)
      logInfo({ context: 'BackendUserProfilesService' }, `Loaded ${response?.users.length ?? 0} user profiles`)
      return response?.users ?? []
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'getAllUsers' })
      return []
    }
  },

  async getMyProfile(): Promise<UserProfile | null> {
    try {
      return await apiRequest<UserProfile>('/users/me')
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'getMyProfile' })
      return null
    }
  },

  async getUserById(userId: string): Promise<UserProfile | null> {
    try {
      return await apiRequest<UserProfile>(`/users/${userId}`)
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'getUserById', userId })
      return null
    }
  },

  async updateUser(userId: string, data: UserProfileUpdate): Promise<UserProfile | null> {
    try {
      const updated = await apiRequest<UserProfile>(`/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      if (updated) {
        logInfo({ context: 'BackendUserProfilesService' }, `Updated user profile: ${userId}`)
      }
      return updated
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'updateUser', userId })
      throw error
    }
  },

  async deactivateUser(userId: string): Promise<boolean> {
    try {
      await apiRequest(`/users/${userId}`, { method: 'DELETE' })
      logInfo({ context: 'BackendUserProfilesService' }, `Deactivated user: ${userId}`)
      return true
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'deactivateUser', userId })
      throw error
    }
  },

  async reactivateUser(userId: string): Promise<UserProfile | null> {
    try {
      const reactivated = await apiRequest<UserProfile>(`/users/${userId}/reactivate`, {
        method: 'POST',
      })
      if (reactivated) {
        logInfo({ context: 'BackendUserProfilesService' }, `Reactivated user: ${userId}`)
      }
      return reactivated
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'reactivateUser', userId })
      throw error
    }
  },

  // ============== Clients ==============

  async getClients(): Promise<Client[]> {
    try {
      const response = await apiRequest<ClientListResponse>('/users/clients/all')
      logInfo({ context: 'BackendUserProfilesService' }, `Loaded ${response?.clients.length ?? 0} clients`)
      return response?.clients ?? []
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'getClients' })
      return []
    }
  },

  async getClientById(clientId: string): Promise<Client | null> {
    try {
      return await apiRequest<Client>(`/users/clients/${clientId}`)
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'getClientById', clientId })
      return null
    }
  },

  async createClient(data: ClientCreate): Promise<Client | null> {
    try {
      const created = await apiRequest<Client>('/users/clients', {
        method: 'POST',
        body: JSON.stringify(data),
      })
      if (created) {
        logInfo({ context: 'BackendUserProfilesService' }, `Created client: ${data.name}`)
      }
      return created
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'createClient' })
      throw error
    }
  },

  async updateClient(clientId: string, data: ClientUpdate): Promise<Client | null> {
    try {
      const updated = await apiRequest<Client>(`/users/clients/${clientId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      if (updated) {
        logInfo({ context: 'BackendUserProfilesService' }, `Updated client: ${clientId}`)
      }
      return updated
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'updateClient', clientId })
      throw error
    }
  },

  async deleteClient(clientId: string): Promise<boolean> {
    try {
      await apiRequest(`/users/clients/${clientId}`, { method: 'DELETE' })
      logInfo({ context: 'BackendUserProfilesService' }, `Deleted client: ${clientId}`)
      return true
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'deleteClient', clientId })
      throw error
    }
  },

  async requestWebOSAccess(): Promise<{ status: string; message: string }> {
    try {
      const result = await apiRequest<{ status: string; message: string }>('/users/me/request-webos-access', {
        method: 'POST',
      })
      logInfo({ context: 'BackendUserProfilesService' }, 'Requested webOS access')
      return result || { status: 'pending', message: '' }
    } catch (error) {
      logError(error as Error, { context: 'BackendUserProfilesService', method: 'requestWebOSAccess' })
      throw error
    }
  },
}
